// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import { z } from "zod";
import { IsoDateSchema, IsoDateTimeSchema } from "./primitives.js";
import { ConsumptionRecordSchema, ConsumptionSnapshotStatusSchema, ReplacementFoodSchema } from "./history.js";
// ---------------------------------------------------------------------------
// Day ledger (ADR-035, adaptive coach phase 1). The whole-day arithmetic
// behind "can I eat this?": consumed_so_far + planned_remaining =
// projected_total vs. the daily target. Planned-meal consumption derives
// from meal outcomes; off-plan food is logged explicitly with an honest
// source/confidence taxonomy. All math is SERVER-side — clients and Amy
// display it, they never re-derive it.
// ---------------------------------------------------------------------------
export const LedgerSourceSchema = z.enum(["label", "database", "composed", "user_estimate"]);
export const LedgerConfidenceSchema = z.enum(["high", "medium", "low"]);
export const OffPlanEntrySchema = z.object({
    id: z.string(),
    entryDate: IsoDateSchema,
    description: z.string().min(1).max(300),
    kcal: z.number().int().min(0).max(6000),
    proteinG: z.number().min(0).max(500).nullable(),
    carbsG: z.number().min(0).max(1000).nullable(),
    fatG: z.number().min(0).max(500).nullable(),
    source: LedgerSourceSchema,
    confidence: LedgerConfidenceSchema,
    /** Honest uncertainty band; kcal is the tracking value (upper end when conservative). */
    estimatedMinKcal: z.number().int().min(0).nullable(),
    estimatedMaxKcal: z.number().int().min(0).nullable(),
    createdVia: z.enum(["amy", "user"]),
    createdAt: IsoDateTimeSchema,
    /**
     * ADR-078: the planned slot this food REPLACED, when the entry was logged
     * that way. Optional (not just nullable) so a response body stored under
     * an idempotency key before this field existed still parses on replay —
     * same convention as DayLedgerResponseSchema's consumedMeals/remainingDay.
     */
    replaces: z.object({ date: IsoDateSchema, slot: z.string() }).nullable().optional(),
});
export const OffPlanEntryCreateRequestSchema = z.object({
    description: z.string().min(1).max(300),
    kcal: z.number().int().min(0).max(6000),
    proteinG: z.number().min(0).max(500).nullable().optional(),
    carbsG: z.number().min(0).max(1000).nullable().optional(),
    fatG: z.number().min(0).max(500).nullable().optional(),
    source: LedgerSourceSchema,
    confidence: LedgerConfidenceSchema,
    estimatedMinKcal: z.number().int().min(0).nullable().optional(),
    estimatedMaxKcal: z.number().int().min(0).nullable().optional(),
    createdVia: z.enum(["amy", "user"]),
    /**
     * ADR-078: "instead of my planned <meal>" — mirrors
     * DayImpactPreviewRequestSchema.replacesMealId exactly. The server links
     * this entry to that slot, atomically records a `changed` outcome for it,
     * and both writes commit in ONE call (no separate outcome call needed).
     */
    replacesMealId: z.string().max(100).nullable().optional(),
    /** Defaults to today (Europe/Berlin); at most 7 days back — same rule as the preview's `date`. */
    date: IsoDateSchema.nullable().optional(),
});
/** One of today's PLANNED meals as the ledger sees it. */
export const LedgerPlannedMealSchema = z.object({
    mealId: z.string(),
    slot: z.string(),
    title: z.record(z.string(), z.string()).nullable(),
    plannedKcal: z.number(),
    /** Latest recorded outcome, if any. */
    outcome: z.enum(["had", "partial", "skipped", "changed"]).nullable(),
    /** kcal counted as consumed for this meal (0 when un-eaten/skipped/changed). */
    consumedKcal: z.number(),
    /**
     * ADR-056: where `consumedKcal` comes from — the immutable consumption
     * record taken at logging (`recorded`), a deterministic reconstruction
     * (`backfilled`), or an honest gap (`unavailable`: counted as 0, flagged).
     * Absent when the meal has no outcome.
     */
    consumedKcalStatus: ConsumptionSnapshotStatusSchema.nullable().optional(),
    /**
     * ADR-078: off-plan food logged as replacing this slot, from any entry
     * still linked to it — present only when non-empty (matching
     * ConsumptionRecordSchema.replacedBy). Normally accompanies
     * `outcome: "changed"`, but a later correction back to had/skipped can
     * leave a linked entry in place (the entry itself is never touched by a
     * later outcome write) — so clients gate the REPLACEMENT HEADLINE on
     * `outcome === "changed"`, never on this field's presence alone.
     */
    replacedBy: z.array(ReplacementFoodSchema).optional(),
});
/**
 * Step 3 (whole-day protection): the deterministic day flags shared by the
 * ledger AND the pre-eating preview — ONE derivation server-side
 * (services/planning deriveDayFlags), never re-derived by Amy or clients.
 */
export const DayFlagsShape = {
    /** consumed + plannedRemaining — the number every food question is judged against. */
    projectedKcal: z.number(),
    /** target − projected: what fits WITHOUT touching the remaining plan. */
    flexibleKcal: z.number().nullable(),
    /** target − consumed: what's left in total IF the remaining plan may change. */
    remainingBudgetKcal: z.number().nullable(),
    /** Planned meals still ahead (no outcome yet). Optional: pre-Step-3
     * idempotency replays lack the Step-3 fields — absent, never fabricated. */
    mealsAheadCount: z.number().int().optional(),
    /** kcal the still-ahead dinner currently requires (0 when none/eaten). */
    dinnerPlannedKcal: z.number().optional(),
    /** target − consumed − non-dinner remaining: what's actually left FOR dinner. */
    dinnerHeadroomKcal: z.number().nullable().optional(),
    /** Dinner still ahead AND its headroom is below min(plannedDinnerKcal, 400). */
    dinnerAtRisk: z.boolean(),
    /** projected > target, strictly — tolerance is display info, never a softener. */
    targetExceeded: z.boolean().optional(),
    /** Meals still ahead AND flexibleKcal is below half the smallest remaining
     * planned meal — the user can't fit even half of their own smallest
     * upcoming eating occasion. Suppressed while projected is below the floor. */
    lowRemaining: z.boolean().optional(),
    /** projected < the server-enforced calorie safety floor. */
    projectedBelowFloor: z.boolean().optional(),
    /**
     * 2026-09-04 (founder acceptance): the AUTHORITATIVE whole-day state —
     * the ONLY basis for headroom vs safety copy. A positive target −
     * projected (`flexibleKcal`) is NEVER optional room while the projection
     * is below an applicable floor: `below_floor` carries the deficit to the
     * floor (per-meal minimums for a safe rebalance are
     * targetAdaptation.remainingMealBounds.minKcalForFloor);
     * `below_target_above_floor` is ordinary optional headroom;
     * `above_target` is over; `on_target` is exact. null = no target.
     * Clients and Amy relay this state; nobody re-derives it from the sign
     * of flexibleKcal.
     */
    dayState: z
        .object({
        state: z.enum(["on_target", "above_target", "below_target_above_floor", "below_floor"]),
        floorDeficitKcal: z.number().nullable(),
        headroomKcal: z.number().nullable(),
        applicableFloorKcal: z.number().nullable(),
    })
        .nullable()
        .optional(),
    /** 2026-09-03: the AUTHORITATIVE punitive-vs-requested adaptation
     * decision. When the user asks to bring the day closer to target, this
     * is the ONLY source of the safety/feasibility answer — deltaToTarget,
     * how much of the REMAINING food can be reduced without crossing the
     * safe floor (and never more than the delta), and a verdict. Clients and
     * Amy relay it; nobody re-derives floor math. null = no target set. */
    targetAdaptation: z
        .object({
        deltaToTargetKcal: z.number(),
        safeReducibleKcal: z.number(),
        cappedByFloor: z.boolean(),
        verdict: z.enum(["on_target", "safe_adaptation_available", "no_meaningful_adaptation", "would_cross_floor"]),
        /** 2026-09-03b (founder incident: "dinner would need ~484" invented
         * model-side): the DIRECT sizing answer per still-unlogged meal,
         * holding the other remaining meals as planned — maxKcalForTarget =
         * the largest this meal may be for projected <= the NOMINAL target
         * (the user's number; tolerance is planner validity, never user
         * consent); minKcalForFloor = the smallest that keeps the day at/
         * above the safe floor (the floor is a MINIMUM bound — it can never
         * forbid a reduction that stays above it). A candidate replacement
         * satisfies an explicit user cap iff its kcal <= maxKcalForTarget —
         * a comparison, never arithmetic. */
        remainingMealBounds: z
            .array(z.object({
            mealId: z.string(),
            slot: z.string(),
            plannedKcal: z.number(),
            maxKcalForTarget: z.number(),
            minKcalForFloor: z.number(),
            /** ADR-055 acceptance 4: the largest this meal may be for projected <= the accepted range top (the feasibility edge). */
            maxKcalForRange: z.number().optional(),
        }))
            .optional(),
    })
        .nullable()
        .optional(),
    /**
     * ADR-055 acceptance 4 — DAY CAPACITY (authoritative, live-day basis):
     * the FOUR distinct amounts a coach may ever quote, each named for what
     * it is. `neededForFloorKcal` = how much the projected day still lacks to
     * reach the safe minimum (0 when at/above it). `roomToTargetKcal` =
     * target − projected (negative = over the nominal target; NOT a maximum).
     * `roomToRangeTopKcal` = accepted range top − projected: the real
     * feasibility capacity for additional food (negative = a change must
     * remove food). `target − consumed` is deliberately NOT served here.
     */
    dayCapacity: z
        .object({
        basis: z.literal("live_day"),
        consumedKcal: z.number().int(),
        plannedRemainingKcal: z.number().int(),
        projectedKcal: z.number().int(),
        floorKcal: z.number().int().nullable(),
        targetKcal: z.number().int().nullable(),
        rangeBottomKcal: z.number().int().nullable(),
        rangeTopKcal: z.number().int().nullable(),
        neededForFloorKcal: z.number().int().nullable(),
        roomToTargetKcal: z.number().int().nullable(),
        roomToRangeTopKcal: z.number().int().nullable(),
    })
        .nullable()
        .optional(),
};
// ---------------------------------------------------------------------------
// ADR-057 — REMAINING-DAY SCENARIOS: the rest of today as deterministic
// backend truth. One implicit picture ("every other remaining meal as
// planned") was never enough: "skip the snack" needs its own projection,
// deltas and per-meal allowances, or an assistant pairs its advice with the
// keep-all figures (founder evidence 2026-09-05: "skip the snack" + a dinner
// limit computed with the snack still eaten). Every scenario is a complete,
// self-consistent picture; consumers quote figures from ONE scenario.
// ---------------------------------------------------------------------------
export const RemainingDayAssumptionSchema = z.object({
    mealId: z.string(),
    slot: z.string(),
    /** ADR-066: `adjust` = this meal made smaller, still eaten. Never a promise that a portion of that size is cookable — `fit_meal` proves that. */
    action: z.enum(["keep", "skip", "adjust"]),
    /** kcal counted for this meal under the scenario (planned when kept, 0 when skipped, the adjusted size when adjusted). */
    kcal: z.number(),
});
/** Allowance for ONE kept meal with the scenario's OTHER kept meals at their planned values. */
export const RemainingDayMealAllowanceSchema = z.object({
    mealId: z.string(),
    slot: z.string(),
    plannedKcal: z.number(),
    /** Largest size that keeps the scenario's projected day at or under the exact target (null without a target). */
    maxKcalForTarget: z.number().nullable(),
    /** Largest size that keeps the scenario's projected day inside the accepted range (null without a range). */
    maxKcalForRange: z.number().nullable(),
    /** Smallest size that keeps the scenario's projected day at or above the safe floor (null without a floor). */
    minKcalForFloor: z.number().nullable(),
});
export const RemainingDayScenarioSchema = z.object({
    /** `keep_all`, `skip:<mealId>` or `adjust:<mealId>` — the identity a consumer must name when quoting its figures. */
    id: z.string(),
    /**
     * ADR-066 ADAPTATION TRUTH: `adjust_one` exists so that REMOVING a meal is
     * never the only served way to lower the day. Before it, `keep_all` and
     * `skip_one` were the whole vocabulary, so the only picture that improved
     * an over-target day was a skip — which is why an assistant recommended
     * one as compensation (founder evidence, birthday brunch 2026-09-06)
     * despite an explicit prompt rule against it. `adjust_one` is the same
     * kind of self-consistent hypothetical as `skip_one`, never an
     * executability claim.
     */
    kind: z.enum(["keep_all", "skip_one", "adjust_one"]),
    skippedMealId: z.string().nullable(),
    /** The meal made smaller in an `adjust_one` scenario; null for every other kind. */
    adjustedMealId: z.string().nullable().optional(),
    /** Every remaining meal with the action this scenario assumes for it. */
    assumptions: z.array(RemainingDayAssumptionSchema),
    /** Σ kcal of the meals kept under this scenario. */
    remainingKcal: z.number(),
    /** consumed (incl. off-plan) + remainingKcal. */
    projectedKcal: z.number(),
    /** projected − target: positive = over the exact target. */
    deltaToTargetKcal: z.number().nullable(),
    /** projected − accepted range top: positive = above the range. */
    deltaToRangeTopKcal: z.number().nullable(),
    /** projected − accepted range bottom: negative = below the range. */
    deltaToRangeBottomKcal: z.number().nullable(),
    /** projected − safe floor: negative = below the floor. */
    deltaToFloorKcal: z.number().nullable(),
    fitsTarget: z.boolean().nullable(),
    fitsRange: z.boolean().nullable(),
    meetsFloor: z.boolean().nullable(),
    /** Allowances for the meals KEPT in this scenario (a skipped meal has none). */
    perMeal: z.array(RemainingDayMealAllowanceSchema),
});
export const RemainingDaySchema = z.object({
    basis: z.literal("live_day"),
    date: IsoDateSchema,
    /** Eaten so far today: consumption history + off-plan food. */
    consumedKcal: z.number(),
    offPlanKcal: z.number(),
    /** Σ planned kcal of the remaining (mutable) meals. */
    plannedRemainingKcal: z.number(),
    /** consumed + plannedRemaining — the keep-all projection. */
    projectedKcal: z.number(),
    targetKcal: z.number().nullable(),
    floorKcal: z.number().nullable(),
    rangeBottomKcal: z.number().nullable(),
    rangeTopKcal: z.number().nullable(),
    /** What the keep-all projection still lacks to reach the floor (0 = at/above). */
    neededForFloorKcal: z.number(),
    /** target − projected (keep-all): negative = over the exact target. NOT a maximum. */
    roomToTargetKcal: z.number().nullable(),
    /** range top − projected (keep-all): the real capacity before something must change. */
    roomToRangeTopKcal: z.number().nullable(),
    /** Immutable history counted today. */
    consumedMeals: z.array(z.object({ mealId: z.string(), slot: z.string(), outcome: z.enum(["had", "partial", "skipped", "changed"]), consumedKcal: z.number() })),
    /** The mutable rest of the day at its current planned values. */
    remainingMeals: z.array(z.object({ mealId: z.string(), slot: z.string(), plannedKcal: z.number(), servings: z.number() })),
    /** keep_all first, then adjust:<mealId> where a smaller size is meaningful and floor-safe, then skip:<mealId> for every remaining meal — deterministic, ≤ 1 + 2n. Adaptation before removal, in the served order. */
    scenarios: z.array(RemainingDayScenarioSchema),
    /**
     * ADR-066 ADAPTATION TRUTH — the distinction a coach must not blur.
     * `objectiveKcal` is what the user asked BiteBalance to aim for (their
     * target). `validityTopKcal` is the accepted range top: it answers "is
     * this allowed?", never "what should we aim for?", and a day sitting
     * below it is not thereby finished. `feasibility` is the AUTHORITATIVE
     * answer to "can the rest of today still reach the objective?", so an
     * assistant narrates it instead of promising an outcome it has not
     * proven:
     *   at_or_under_objective    — already there; nothing needs removing.
     *   reachable_by_adjustment  — at least one remaining meal can be made
     *                              smaller and stay at or above the floor.
     *   reachable_only_by_removal— only dropping a meal entirely would reach
     *                              it; the objective is NOT worth that, so a
     *                              consumer states the honest trade-off and
     *                              never prescribes the removal itself.
     *   not_reachable            — no scenario reaches the objective while
     *                              respecting the safe floor, whether because
     *                              the floor forbids the reduction or because
     *                              what is already eaten passed the objective.
     *                              Say so plainly and aim for a reasonable day
     *                              instead; never fabricate the room.
     * Null when the user has no target. Derived per request from figures
     * already computed here; nothing new is persisted, and no consumer
     * re-derives it.
     */
    objective: z
        .object({
        objectiveKcal: z.number(),
        validityTopKcal: z.number().nullable(),
        feasibility: z.enum(["at_or_under_objective", "reachable_by_adjustment", "reachable_only_by_removal", "not_reachable"]),
        /** Scenario ids that reach the objective, cheapest adaptation first; empty unless something does. */
        reachedByScenarioIds: z.array(z.string()),
    })
        .nullable(),
});
export const DayLedgerResponseSchema = z.object({
    date: IsoDateSchema,
    targetKcal: z.number().int().nullable(),
    toleranceKcalPct: z.number(),
    /** The user's stored goal — the arithmetic is goal-neutral; interpretation
     * (is "over target" a warning or the point?) follows this. */
    goal: z.enum(["lose_weight", "maintain", "gain_muscle", "improve_health"]).nullable().optional(),
    /** Server-enforced calorie safety floor (sex-based / ADR-030 breastfeeding). */
    safeFloorKcal: z.number().int().nullable().optional(),
    /** Eaten so far: outcome-recorded planned meals + off-plan entries. */
    consumedKcal: z.number(),
    /** Planned meals still ahead (no outcome yet). */
    plannedRemainingKcal: z.number(),
    /** 2026-09-04: what the plan intended for the WHOLE day (Σ planned meals). */
    plannedTodayKcal: z.number().optional(),
    /**
     * 2026-09-04: the authoritative planned-vs-projected relationship, so the
     * UI can say "projected differs from your plan because …" without
     * inference. kcal = projected − plannedToday; reasons = every recorded
     * outcome whose consumed kcal differs from its planned kcal (partial /
     * skipped / changed, or a "had" with different actual servings), with the
     * signed per-meal delta; offPlanKcal = logged off-plan food (adds).
     * Σ reasons.deltaKcal + offPlanKcal = kcal by construction.
     */
    projectionDelta: z
        .object({
        kcal: z.number(),
        reasons: z.array(z.object({
            mealId: z.string(),
            slot: z.string(),
            outcome: z.enum(["had", "partial", "skipped", "changed"]),
            plannedKcal: z.number(),
            consumedKcal: z.number(),
            deltaKcal: z.number(),
        })),
        offPlanKcal: z.number(),
    })
        .optional(),
    ...DayFlagsShape,
    plannedMeals: z.array(LedgerPlannedMealSchema),
    offPlanEntries: z.array(OffPlanEntrySchema),
    /**
     * ADR-056: the day's canonical consumption records (one per slot) — the
     * immutable history `consumedKcal` is summed from. Includes records whose
     * slot is no longer in the current plan. Optional so ledgers stored before
     * the field existed (idempotency replays) still parse.
     */
    consumedMeals: z.array(ConsumptionRecordSchema).optional(),
    /**
     * ADR-057: the rest of today as deterministic scenarios (keep-all and
     * skip-one) — the only source an assistant may quote remaining-day
     * figures from. Optional so ledgers stored before the field existed
     * (idempotency replays) still parse.
     */
    remainingDay: RemainingDaySchema.optional(),
});
// ---------------------------------------------------------------------------
// Step 3 pre-eating preview: POST /v1/ledger/preview — "can I eat X?"
// answered BEFORE X is logged. Strictly non-mutating.
// ---------------------------------------------------------------------------
export const DayImpactPreviewRequestSchema = z.object({
    /** Tracking value for the proposed food (conservative/upper estimate). */
    kcal: z.number().int().min(0).max(6000),
    estimatedMinKcal: z.number().int().min(0).nullable().optional(),
    estimatedMaxKcal: z.number().int().min(0).nullable().optional(),
    /** "Instead of my planned <meal>": that meal leaves plannedRemaining. */
    replacesMealId: z.string().max(100).nullable().optional(),
    /** Defaults to today (Europe/Berlin); at most 7 days back. */
    date: IsoDateSchema.nullable().optional(),
});
/** The projected day-state IF the user ate the proposed food. */
export const DayImpactSchema = z.object({
    date: IsoDateSchema,
    targetKcal: z.number().int().nullable(),
    toleranceKcalPct: z.number(),
    goal: z.enum(["lose_weight", "maintain", "gain_muscle", "improve_health"]).nullable(),
    safeFloorKcal: z.number().int().nullable(),
    proposedKcal: z.number().int(),
    replacedMealId: z.string().nullable(),
    /** AFTER the proposed food: consumed so far + proposed. */
    consumedKcal: z.number(),
    plannedRemainingKcal: z.number(),
    ...DayFlagsShape,
    /** Estimate band carried through — never pretend an estimated food is exact. */
    projectedMinKcal: z.number().nullable(),
    projectedMaxKcal: z.number().nullable(),
});
export const DayImpactPreviewResponseSchema = z.object({
    /** The CURRENT day ledger (nothing logged). */
    ledger: DayLedgerResponseSchema,
    /** The projected day-state if the user ate the proposed food. */
    impact: DayImpactSchema,
});
export const OffPlanEntryCreateResponseSchema = z.object({
    entry: OffPlanEntrySchema,
    ledger: DayLedgerResponseSchema,
    /**
     * ADR-078 proof-of-change (mirrors ADR-075: never claim a change happened
     * without a server-computed proof). Non-null only when `replacesMealId`
     * was honored — the exact slot the write changed and the outcome row it
     * produced. Null for an ordinary (non-replacing) off-plan entry.
     */
    replacedMeal: z
        .object({
        mealId: z.string(),
        date: IsoDateSchema,
        slot: z.string(),
        outcomeRecordId: z.string(),
    })
        .nullable()
        .optional(),
});
//# sourceMappingURL=ledger.js.map